import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import EventEmitter from "./events.js";
import { memo, useRef, useState, useEffect, createContext } from "react";
import { useGenerateStaticTags } from "./hooks.js";
import { createRoot } from "react-dom/client";
import Renderer from "./renderer.js";
import Toolbar from "../ui/components/Toolbar.js";
import error from "./utils/error.js";
import createTag from "./utils/createTag.js";
import flattenTagValue from "./utils/flattenTagValue.js";
import tagToValues from "./utils/tagToValues.js";
import mergeDeep from "./utils/mergeDeep.js";
export const defaults = {
    mode: "dynamic",
    determineVisibility: () => window &&
        new URLSearchParams(window.location.search).get("mode") === "edit",
    toolbarOptions: {
        position: "center",
    },
};
export default class Launched {
    renderer = new Renderer();
    addTag = () => { };
    originalTags = new Map();
    version = -1;
    setCanUndo = () => { };
    setCanRedo = () => { };
    history = [];
    config;
    tags = {};
    uploadImage;
    Provider;
    context = createContext({});
    static instance;
    static events = new EventEmitter();
    constructor(config) {
        if (Launched.instance) {
            error("There can only be one instance of Launched.");
        }
        Launched.instance = this;
        this.config = mergeDeep(defaults, config ?? {});
        this.uploadImage = this.config.onImageUpload;
        this.Provider = ({ children }) => {
            const [canUndo, setCanUndo] = useState(false);
            const [canRedo, setCanRedo] = useState(false);
            const [visible] = useState(() => {
                const visible = this.config.determineVisibility(this);
                if (!visible)
                    this.config.locked = true;
                return visible;
            });
            const [tags, setTags] = useState({});
            this.tags = Object.fromEntries(Object.entries(tags).map(([key, data]) => {
                const setData = (value, config) => {
                    if (!tags[key] || this.config.locked)
                        return;
                    setTags((p) => {
                        const newValue = typeof value === "function" ? value(p[key].data.value) : value;
                        if (!config?.silent)
                            Launched.events.emit("tag:change", key, newValue, p[key]?.data.value);
                        const newTags = { ...p };
                        const tag = newTags[key];
                        if (tag) {
                            tag.data = { ...tag.data, value: newValue };
                        }
                        return newTags;
                    });
                };
                return [key, { ...data, setData }];
            }));
            this.addTag = (key, tag) => {
                setTags((p) => ({ ...p, [key]: tag }));
            };
            useEffect(() => {
                Launched.events.emit("data:update", this.tags);
            }, [tags]);
            useEffect(() => {
                this.setCanUndo = setCanUndo;
                this.setCanRedo = setCanRedo;
            }, []);
            return (_jsxs(this.context.Provider, { value: {
                    useTag: this.useTag.bind(this),
                }, children: [children, visible && (_jsx(Toolbar, { ...this.config.toolbarOptions, undo: this.undo.bind(this), redo: this.redo.bind(this), revert: this.restore.bind(this, true), save: () => this.config.save?.(Object.fromEntries(Object.entries(this.tags).map(([key, tag]) => [key, tagToValues(tag)]))), canUndo: canUndo, canRedo: canRedo }))] }));
        };
        Launched.events.on("tag:ready", (...props) => {
            console.log("rendering", props[0]);
            this.render(props[0], props[2]);
        });
        Launched.events.on("tag:change", (key, value) => {
            if (this.version !== this.history.length - 1) {
                this.history = this.history.slice(0, this.version + 1);
                this.setCanRedo(false);
            }
            this.version++;
            this.history.push({ key: String(key), value });
            this.setCanUndo(true);
        });
        if (this.config.mode === "static") {
            const content = document.body.innerHTML;
            // ! Ugly hack to avoid generating another root component
            const Raw = memo(({ value }) => {
                const ref = useRef(null);
                const [, set] = useState(0);
                useEffect(() => {
                    ref.current.outerHTML = value;
                    set(1);
                }, []);
                useGenerateStaticTags(this.config.mode !== "static" || !ref.current);
                return _jsx("div", { ref: ref });
            });
            const root = createRoot(document.body);
            root.render(_jsx(this.Provider, { children: _jsx(Raw, { value: content }) }));
        }
    }
    useTag = ((key, value, options) => {
        const t = this ?? Launched.instance;
        let tag = t.tags[key];
        if (!tag && value != null) {
            const newTag = createTag(value, options?.type ??
                (Array.isArray(value) ? typeof value[0] : typeof value));
            setTimeout(() => this.addTag(String(key), newTag), 0);
            tag = newTag;
        }
        else if (!tag)
            error(`Tag "${String(key)}" does not exist. Try providing a value to useTag.`);
        const v = typeof tag.data.value === "object"
            ? flattenTagValue(tag.data.value)
            : tag.data.value;
        return [
            v,
            (el) => {
                if (!el || tag.el.current)
                    return;
                tag.el.current = el;
                if (!this.originalTags.has(key))
                    this.originalTags.set(key, tag.data.value);
                const o = {
                    ...options,
                    arrayMutable: options?.arrayMutable ?? this.config.arraysMutable,
                };
                Launched.events.emit("tag:ready", key, tag, o);
            },
        ];
    });
    render(tag, options) {
        if (!tag || !this.tags[tag])
            return;
        const dry = options && this.config.locked;
        this.renderer.renderSingleTagUI(this.tags[tag], String(tag), options, dry);
    }
    static lock() {
        if (!Launched.instance)
            error("Launched is not initialized.");
        Launched.instance.config.locked = true;
        function unmountTag(id, value, type) {
            if (type === "object") {
                Object.keys(value).forEach((key) => {
                    Launched.instance.renderer.unmountSingleTagUI(`${id}-${key}`);
                });
            }
            else
                Launched.instance.renderer.unmountSingleTagUI(id);
        }
        Object.entries(Launched.instance.tags).map(([key, tag]) => {
            if (!Array.isArray(tag.data.value))
                unmountTag(key, tag.data.value, tag.data.type);
            else
                tag.data.value.forEach((_, i) => {
                    unmountTag(`${key}-${i}`, tag.data.value[i], tag.data.type);
                });
        });
        Launched.events.emit("data:lock");
    }
    static unlock() {
        if (!Launched.instance)
            error("Launched is not initialized.");
        Launched.instance.config.locked = false;
        Object.entries(Launched.instance.tags).map(([key]) => {
            Launched.instance.render(key);
        });
        Launched.events.emit("data:unlock");
    }
    static toggle() {
        if (!Launched.instance)
            error("Launched is not initialized.");
        Launched.instance.config.locked ? Launched.unlock() : Launched.lock();
    }
    static isVisible() {
        if (!Launched.instance)
            error("Launched is not initialized.");
        return Launched.instance.config.determineVisibility(Launched.instance);
    }
    static registerTagFormat(name, renderer) {
        Renderer.registerTagFormat(name, renderer);
    }
    undo() {
        if (this.version === -1 || this.config.locked)
            return;
        else if (this.version === 0) {
            this.version = -1;
            this.restore();
            this.setCanUndo(false);
            this.setCanRedo(true);
            return;
        }
        const { key, value } = this.history[--this.version];
        Launched.events.emit("data:undo", value, this.tags[key].data.value);
        this.tags[key].setData(value, { silent: true });
        this.setCanRedo(true);
    }
    redo() {
        if (!this.history.length ||
            this.version === this.history.length - 1 ||
            this.config.locked)
            return;
        const { key, value } = this.history[++this.version];
        Launched.events.emit("data:redo", value, this.tags[key].data.value);
        this.tags[key].setData(value, { silent: true });
        this.setCanUndo(true);
        if (this.version === this.history.length - 1)
            this.setCanRedo(false);
    }
    restore(hard) {
        if (this.config.locked)
            return;
        if (hard)
            this.history = [];
        this.version = -1;
        this.setCanUndo(false);
        this.setCanRedo(false);
        Launched.events.emit("data:restore", this.originalTags, this.tags);
        Array.from(this.originalTags.entries()).map(([key, value]) => {
            if (this.tags[key]?.data.value !== value) {
                this.tags[key].setData(value, { silent: true });
            }
        });
    }
}
export function LaunchedProvider({ config, children, }) {
    const L = Launched.instance ?? new Launched(config);
    return _jsx(L.Provider, { children: children });
}
